import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo, useCallback } from "react";
import { fetchData, post, patch, del } from "@/utils/api-handler";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import type { CommandPromptItem } from "@/components/aios-command/chat/command-prompts";

const PRESETS_PER_PAGE = 20;
/** API caps `limit` on GET /preset-sub-categories (e.g. le=50). */
const COMMAND_PRESET_LIST_OR_VIEW_PERMISSIONS = [
  "LIST_COMMAND_PRESETS",
  "VIEW_COMMAND_PRESET",
];
const COMMAND_PRESET_CATEGORY_READ_PERMISSIONS = [
  "LIST_COMMAND_PRESETS",
  "VIEW_COMMAND_PRESET",
];

function presetToPromptItem(preset: Preset): CommandPromptItem {
  return {
    id: preset.id,
    title: preset.title,
    prompt: preset.persona,
    description: preset.description,
    agent_name: preset.agent_name ?? undefined,
    agent_description: preset.agent_description ?? undefined,
    tags: preset.tags,
    parent_category: preset.parent_category_name ?? preset.parent_category,
  };
}

export type PresetQueryParentCategory =
  | "Executive"
  | "Management"
  | "SEE"
  | "DO"
  | "EXECUTIVE"
  | "MANAGEMENT";

/** POST/PATCH preset body: maps to SEE | DO for APIs that still expect legacy values. */
export function toApiParentCategory(
  c?: PresetQueryParentCategory | string | null
): string | undefined {
  if (c == null || c === "") return undefined;
  const u = String(c).toUpperCase();
  if (u === "EXECUTIVE" || u === "SEE" || c === "Executive") return "SEE";
  if (u === "MANAGEMENT" || u === "DO" || c === "Management") return "DO";
  return undefined;
}

/** Normalize parent category label/name for legacy callers (e.g. EXECUTIVE / SEE / Executive). */
export function parentCategoryForPresetsListParam(
  c?: string | null
): string | undefined {
  if (c == null || c === "") return undefined;
  const t = String(c).trim();
  if (!t) return undefined;
  const u = t.toUpperCase();
  if (u === "EXECUTIVE" || u === "SEE" || t === "Executive") return "EXECUTIVE";
  if (u === "MANAGEMENT" || u === "DO" || t === "Management") return "MANAGEMENT";
  return t;
}

export interface PresetParentCategoryItem {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface PresetParentCategoryListResponse {
  item_count: number;
  page_count: number;
  items: PresetParentCategoryItem[];
}

/** Pill label from API name, e.g. `EXECUTIVE` → `Executive`. */
export function presetParentCategoryPillLabel(apiName: string): string {
  const t = apiName.trim();
  if (!t) return t;
  const lower = t.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function usePresetParentCategories(options?: { enabled?: boolean }) {
  const { enabled = true } = options ?? {};
  const { hasPermission: canListPresetParentCategories } = useProtectedQuery({
    queryKey: ["_gated_list_preset_parent_categories"],
    queryFn: async () => null,
    requiredPermissions: COMMAND_PRESET_CATEGORY_READ_PERMISSIONS,
    requireAll: false,
    enabled: false,
  });

  return useQuery({
    queryKey: ["preset-parent-categories"],
    queryFn: () =>
      fetchData(
        "/api/command-team/preset-parent-categories?page=1&limit=50"
      ) as Promise<PresetParentCategoryListResponse>,
    staleTime: 5 * 60 * 1000,
    enabled: enabled && canListPresetParentCategories,
  });
}

export function parentCategoryDisplayLabel(c: string | undefined): string {
  if (c == null || c === "") return "";
  if (c === "Management" || c === "DO") return "Do";
  if (c === "Executive" || c === "SEE") return "See";
  return c;
}

/** UI label for preset category (API may return SEE | DO or Executive | Management). */
/** Label for filter pills (All | Executive | Management keys). */
export function parentCategoryFilterLabel(
  cat: "all" | "Executive" | "Management"
): string {
  if (cat === "all") return "All";
  return parentCategoryDisplayLabel(cat);
}

/** Form dropdown only uses Executive | Management; API may return SEE | DO. */
export function apiParentCategoryToFormSelect(
  c: string | undefined
): "Executive" | "Management" {
  if (c === "Management" || c === "DO") return "Management";
  return "Executive";
}

/** Body for POST/PATCH /presets — sends `parent_category_id` only (no `parent_category` name). */
export function presetFormToApiPayload(data: {
  title: string;
  description: string;
  persona: string;
  tags: string[];
  visibility: "PUBLIC" | "PRIVATE";
  parent_category: string;
  parent_category_id: string;
}) {
  const parent_category_id = data.parent_category_id?.trim();
  if (!parent_category_id) {
    throw new Error("parent_category_id is required");
  }
  return {
    title: data.title,
    description: data.description,
    persona: data.persona,
    tags: data.tags,
    visibility: data.visibility,
    parent_category_id,
  };
}

/** Map stored preset `parent_category` to a row `name` from preset-parent-categories for form controls. */
export function presetParentCategoryToFormValue(
  stored: string | undefined,
  items: PresetParentCategoryItem[]
): string {
  if (!items.length) return (stored ?? "").trim();
  const s = (stored ?? "").trim();
  if (!s) return items[0]?.name ?? "";
  if (items.some((i) => i.name === s)) return s;
  const mapped = parentCategoryForPresetsListParam(s);
  if (!mapped) return items[0]?.name ?? s;
  const hit = items.find(
    (i) => i.name.toUpperCase() === mapped.toUpperCase()
  );
  return hit?.name ?? items[0]?.name ?? s;
}

/** Resolve preset parent row `id` for forms (prefers API `parent_category_id` when present). */
export function presetParentCategoryIdToFormValue(
  storedName: string | undefined,
  storedId: string | undefined,
  items: PresetParentCategoryItem[]
): string {
  if (!items.length) return (storedId ?? "").trim();
  const sid = (storedId ?? "").trim();
  if (sid && items.some((i) => i.id === sid)) return sid;
  const nameKey = presetParentCategoryToFormValue(storedName, items);
  return items.find((i) => i.name === nameKey)?.id ?? items[0]?.id ?? "";
}

interface UsePresetsOptions {
  /** Row id from GET /preset-parent-categories — passed as `parent_category_id` query param. */
  parentCategoryId?: string;
  search?: string;
  myPresets?: boolean;
  enabled?: boolean;
  /** Scroll container for preset cards — IntersectionObserver root for horizontal infinite scroll. */
  intersectionRoot?: Element | null;
}

export function usePresets(options: UsePresetsOptions = {}) {
  const { parentCategoryId, search, myPresets, enabled = true, intersectionRoot } = options;
  const { hasPermission: canListPresets } = useProtectedQuery({
    queryKey: ["_gated_list_command_presets"],
    queryFn: async () => null,
    requiredPermissions: COMMAND_PRESET_LIST_OR_VIEW_PERMISSIONS,
    requireAll: false,
    enabled: false,
  });

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
    root: intersectionRoot ?? undefined,
  });

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<PresetListResponse>({
    queryKey: ["user-presets", parentCategoryId, search, myPresets],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.append("page", String(pageParam));
      params.append("limit", String(PRESETS_PER_PAGE));
      params.append("sort_by", "created_at");
      params.append("sort_order", "desc");

      const pcid = parentCategoryId?.trim();
      if (pcid) params.append("parent_category_id", pcid);
      if (search?.trim()) params.append("search", search.trim());
      if (myPresets) params.append("my_presets", "true");

      return fetchData(`/api/command-team/presets?${params.toString()}`);
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < lastPage.page_count) {
        return pages.length + 1;
      }
      return undefined;
    },
    enabled: enabled && canListPresets,
  });

  const allPresets = useMemo(() => {
    const items = data?.pages.flatMap((page) => page.items) ?? [];
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [data]);

  const allPromptItems = useMemo(
    () => allPresets.map(presetToPromptItem),
    [allPresets]
  );

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    presets: allPresets,
    promptItems: allPromptItems,
    canListPresets,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    loadMoreRef,
    totalCount: data?.pages[0]?.item_count ?? allPresets.length,
  };
}

export function useFavoritePresets(options: UsePresetsOptions = {}) {
  const { parentCategoryId, search, enabled = true } = options;
  const { hasPermission: canListFavoritePresets } = useProtectedQuery({
    queryKey: ["_gated_list_favorite_command_presets"],
    queryFn: async () => null,
    requiredPermissions: [
      "FAVORITE_COMMAND_PRESET",
      ...COMMAND_PRESET_LIST_OR_VIEW_PERMISSIONS,
    ],
    requireAll: false,
    enabled: false,
  });

  const { data, isLoading } = useInfiniteQuery<PresetListResponse>({
    queryKey: [
      "user-preset-favorites",
      parentCategoryId,
      search,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.append("page", String(pageParam));
      params.append("limit", String(PRESETS_PER_PAGE));
      params.append("sort_by", "created_at");
      params.append("sort_order", "desc");

      const pcid = parentCategoryId?.trim();
      if (pcid) params.append("parent_category_id", pcid);
      if (search?.trim()) params.append("search", search.trim());

      return fetchData(
        `/api/command-team/presets/favorites?${params.toString()}`
      );
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < lastPage.page_count) {
        return pages.length + 1;
      }
      return undefined;
    },
    enabled: enabled && canListFavoritePresets,
  });

  const allFavorites = useMemo(() => {
    const items = data?.pages.flatMap((page) => page.items) ?? [];
    const seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [data]);

  const favoriteIds = useMemo(
    () => new Set(allFavorites.map((p) => p.id)),
    [allFavorites]
  );

  const favoritePromptItems = useMemo(
    () => allFavorites.map(presetToPromptItem),
    [allFavorites]
  );

  return {
    favorites: allFavorites,
    favoriteIds,
    favoritePromptItems,
    canListFavoritePresets,
    isLoading,
    totalCount: data?.pages[0]?.item_count ?? allFavorites.length,
  };
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();

  const mutation = useProtectedMutation({
    mutationFn: (presetId: string) =>
      post(`/api/command-team/presets/${presetId}/favorite`, {}),
    requiredPermissions: ["FAVORITE_COMMAND_PRESET"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-presets"] });
      queryClient.invalidateQueries({ queryKey: ["user-preset-favorites"] });
    },
  });

  const toggleFavorite = useCallback(
    (presetId: string) => {
      mutation.mutate(presetId);
    },
    [mutation]
  );

  return {
    toggleFavorite,
    isPending: mutation.isPending,
    hasPermission: mutation.hasPermission,
  };
}

export function useCreatePreset() {
  const queryClient = useQueryClient();

  return useProtectedMutation({
    mutationFn: (data: PresetFormData) =>
      post("/api/command-team/presets", presetFormToApiPayload(data)),
    requiredPermissions: ["CREATE_COMMAND_PRESET"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-presets"] });
      queryClient.invalidateQueries({ queryKey: ["user-preset-favorites"] });
    },
  });
}

export function useUpdatePreset() {
  const queryClient = useQueryClient();

  return useProtectedMutation({
    mutationFn: ({ id, data }: { id: string; data: PresetFormData }) =>
      patch(`/api/command-team/presets/${id}`, presetFormToApiPayload(data)),
    requiredPermissions: ["UPDATE_COMMAND_PRESET"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-presets"] });
      queryClient.invalidateQueries({ queryKey: ["user-preset-favorites"] });
    },
  });
}

export function useDeletePreset() {
  const queryClient = useQueryClient();

  return useProtectedMutation({
    mutationFn: (id: string) => del(`/api/command-team/presets/${id}`),
    requiredPermissions: ["DELETE_COMMAND_PRESET"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-presets"] });
      queryClient.invalidateQueries({ queryKey: ["user-preset-favorites"] });
    },
  });
}
